import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, CalendarRange, Flame } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import TablePagination from "../components/TablePagination";

const MONTHS = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];
const WEEKDAYS = ["Ahd", "Isn", "Sel", "Rab", "Kha", "Jum", "Sab"];
const DAYS = 14;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function VehicleAvailability() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("semua");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [start, setStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/bookings").then((r) => {
      setAllBookings(r.data.filter((b) => ["diluluskan", "selesai", "menunggu_kelulusan"].includes(b.status) && b.vehicle_id));
      setBookings(r.data.filter((b) => ["diluluskan", "selesai"].includes(b.status) && b.vehicle_id));
    });
  }, []);
  useEffect(() => { setPage(1); }, [search, category]);

  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  }), [start]);

  const byVehicle = useMemo(() => {
    const map = {};
    for (const b of bookings) (map[b.vehicle_id] = map[b.vehicle_id] || []).push(b);
    return map;
  }, [bookings]);

  const categories = [...new Set(vehicles.map((v) => v.category))].sort();
  const filtered = vehicles.filter((v) => {
    const q = search.toLowerCase();
    const m = !q || `${v.registration_number} ${v.make} ${v.model}`.toLowerCase().includes(q);
    return m && (category === "semua" || v.category === category);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const cellFor = (v, d) => {
    if (v.status === "penyelenggaraan") return { cls: "bg-amber-400/80 stripe-cell", title: "Penyelenggaraan" };
    if (v.status === "dilupus") return { cls: "bg-slate-300", title: "Dilupus" };
    const key = iso(d);
    const b = (byVehicle[v.id] || []).find((x) => (x.start_datetime || "").slice(0, 10) <= key && key <= (x.end_datetime || "").slice(0, 10));
    if (b) {
      return b.status === "selesai"
        ? { cls: "bg-sky-300 hover:bg-sky-400 cursor-pointer", title: `Selesai: ${b.driver_name} → ${b.destination}`, booking: b }
        : { cls: "bg-[#10056E] hover:bg-[#2a17a8] cursor-pointer", title: `Ditempah: ${b.driver_name} → ${b.destination}`, booking: b };
    }
    return { cls: "bg-emerald-300/70 hover:bg-emerald-300", title: "Tersedia" };
  };

  const todayIso2 = iso(new Date());
  const busyToday = useMemo(() => new Set(bookings.filter((b) => b.status === "diluluskan" &&
    (b.start_datetime || "").slice(0, 10) <= todayIso2 && todayIso2 <= (b.end_datetime || "").slice(0, 10)).map((b) => b.vehicle_id)), [bookings, todayIso2]);
  const fleet = vehicles.filter((v) => v.status !== "dilupus");
  const maint = fleet.filter((v) => v.status === "penyelenggaraan").length;
  const usedToday = fleet.filter((v) => busyToday.has(v.id)).length;
  const freeToday = fleet.length - maint - usedToday;

  const ym = todayIso2.slice(0, 7);
  const yy = todayIso2.slice(0, 4);
  const util = useMemo(() => {
    const month = {}, year = {};
    for (const b of allBookings) {
      const k = (b.start_datetime || "").slice(0, 7);
      if (!b.vehicle_reg) continue;
      if (k === ym) month[b.vehicle_reg] = (month[b.vehicle_reg] || 0) + 1;
      if (k.slice(0, 4) === yy) year[b.vehicle_reg] = (year[b.vehicle_reg] || 0) + 1;
    }
    return { month, year };
  }, [allBookings, ym, yy]);

  const rank = (counts) => {
    const rows = fleet.map((v) => ({ nama: v.registration_number, n: counts[v.registration_number] || 0 }));
    const sorted = [...rows].sort((a, b) => b.n - a.n);
    return { top: sorted.slice(0, 3), least: sorted.slice(-3).reverse() };
  };
  const mRank = rank(util.month);
  const yRank = rank(util.year);

  const shift = (n) => setStart((s) => { const d = new Date(s); d.setDate(d.getDate() + n); return d; });
  const todayIso = iso(new Date());

  return (
    <div className="space-y-6 fade-up" data-testid="vehicle-availability-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><CalendarRange className="h-6 w-6 text-[#10056E]" /> Ketersediaan Kenderaan</h1>
        <p className="text-sm text-muted-foreground mt-1">Garis masa {DAYS} hari — {filtered.length} kenderaan. Klik sel biru untuk melihat tempahan.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="availability-stats">
        {[
          { label: "Tersedia Hari Ini", value: freeToday, cls: "border-emerald-300 bg-emerald-50", txt: "text-emerald-700" },
          { label: "Sedang Digunakan", value: usedToday, cls: "border-[#10056E]/30 bg-indigo-50", txt: "text-[#10056E]" },
          { label: "Penyelenggaraan", value: maint, cls: "border-amber-300 bg-amber-50", txt: "text-amber-700" },
          { label: "Kadar Ketersediaan", value: `${fleet.length ? Math.round((freeToday / fleet.length) * 100) : 0}%`, cls: "border-slate-200 bg-white", txt: "text-slate-800" },
        ].map((s) => (
          <div key={s.label} className={`border rounded-xl px-4 py-3 ${s.cls}`}>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-bold ${s.txt}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="availability-utilization">
        {[{ t: "Bulan Ini", r: mRank }, { t: "Tahun Ini", r: yRank }].map(({ t, r }) => (
          <div key={t} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <p className="text-sm font-bold flex items-center gap-2"><Flame className="h-4 w-4 text-[#DC3545]" /> Penggunaan {t}</p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-[#DC3545]">Paling Digunakan</p>
                {r.top.map((x) => (
                  <div key={x.nama} className="flex items-center justify-between text-xs mt-1.5">
                    <span className="font-semibold">{x.nama}</span>
                    <span className="font-bold text-[#DC3545] bg-red-50 border border-red-200 rounded-full px-2 py-0.5">{x.n}x</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600">Paling Kurang</p>
                {r.least.map((x) => (
                  <div key={x.nama} className="flex items-center justify-between text-xs mt-1.5">
                    <span className="font-semibold">{x.nama}</span>
                    <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{x.n}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(-7)} data-testid="timeline-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setStart(d); }} data-testid="timeline-today-btn">Hari Ini</Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(7)} data-testid="timeline-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            <p className="text-sm font-semibold ml-1" data-testid="timeline-range-label">
              {days[0].getDate()} {MONTHS[days[0].getMonth()]} — {days[DAYS - 1].getDate()} {MONTHS[days[DAYS - 1].getMonth()]} {days[DAYS - 1].getFullYear()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 w-44" data-testid="timeline-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                <SelectItem value="semua">Semua Kategori</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative w-52">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Cari plat / model..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="timeline-search-input" />
            </div>
          </div>
        </div>

        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />

        <div className="px-4 py-2 flex flex-wrap gap-4 text-[11px] text-muted-foreground border-b">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-300/70 border border-emerald-400" /> Tersedia</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#10056E]" /> Ditempah (Diluluskan)</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-sky-300" /> Selesai</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-400/80" /> Penyelenggaraan</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-slate-300" /> Dilupus</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-50/70">
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-1.5 sticky left-0 bg-slate-50 z-10 w-56 border-b">Kenderaan</th>
                {days.map((d) => (
                  <th key={iso(d)} className={`text-center text-[10px] font-semibold px-1 py-1.5 border-b border-l ${iso(d) === todayIso ? "bg-indigo-50 text-[#10056E]" : "text-muted-foreground"}`}>
                    {WEEKDAYS[d.getDay()]}<br />{d.getDate()} {MONTHS[d.getMonth()]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={DAYS + 1} className="text-center text-sm text-muted-foreground py-10">Tiada kenderaan dijumpai.</td></tr>
              )}
              {paged.map((v) => (
                <tr key={v.id} className="border-b last:border-b-0 hover:bg-slate-50/50" data-testid={`timeline-row-${v.registration_number}`}>
                  <td className="px-3 py-1 sticky left-0 bg-white z-10 border-r">
                    <button className="text-left" onClick={() => navigate(`/kenderaan/${v.id}`)}>
                      <p className="text-[13px] font-bold leading-tight">{v.registration_number}
                        <span className={`ml-2 text-[10px] font-bold rounded-full px-1.5 py-px border ${(util.month[v.registration_number] || 0) >= 4 ? "text-[#DC3545] bg-red-50 border-red-200" : (util.month[v.registration_number] || 0) === 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-600 bg-slate-50 border-slate-200"}`}>
                          {util.month[v.registration_number] || 0}x bln ini
                        </span>
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{v.make} {v.model} · {v.category}</p>
                    </button>
                  </td>
                  {days.map((d) => {
                    const c = cellFor(v, d);
                    return (
                      <td key={iso(d)} className="border-l p-px">
                        <div title={c.title}
                          onClick={() => c.booking && navigate(`/tempahan/${c.booking.id}`)}
                          className={`h-6 rounded-sm transition-colors ${c.cls}`} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
